import { toast } from 'sonner'
import { useEffect, useState } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { api } from '@/lib/api'
import { EmptyState } from '@/components/shared/EmptyState'
import { Calendar, Clock, Trash2, Loader2, ChevronLeft, ChevronRight, Send } from 'lucide-react'

interface ScheduledMessage {
  id: string
  from_email: string
  to: string[]
  subject: string
  status: string
  scheduled_at: string | null
  sent_at: string | null
  thread_id: string | null
}

// CHECKPOINT: PRD-04 FR-4.3.5 / PRD-10 — Dashboard scheduled-emails list with cancel action.
export function ScheduledEmails() {
  const [messages, setMessages] = useState<ScheduledMessage[]>([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [cancelId, setCancelId] = useState<string | null>(null)
  const [canceling, setCanceling] = useState(false)

  const fetchMessages = async () => {
    try {
      const res = await api.get(`/outbound/messages?status=scheduled&page=${page}&per_page=20`)
      setMessages(res.data.messages || [])
      setTotal(res.data.pagination?.total || 0)
      setTotalPages(res.data.pagination?.total_pages || 1)
    } catch {
      toast.error('Failed to load scheduled emails')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchMessages()
  }, [page])

  const handleCancel = async () => {
    if (!cancelId) return
    setCanceling(true)
    try {
      await api.delete(`/outbound/schedule/${cancelId}`)
      toast.success('Scheduled email canceled')
      setCancelId(null)
      fetchMessages()
    } catch {
      toast.error('Failed to cancel scheduled email')
    } finally {
      setCanceling(false)
    }
  }

  const formatScheduledTime = (dateStr: string | null) => {
    if (!dateStr) return 'Unknown'
    const date = new Date(dateStr)
    return date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  if (loading) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="h-8 w-32 bg-accent rounded" />
        <div className="h-64 bg-accent rounded-xl" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Scheduled Emails</h1>
        <p className="text-sm text-muted-foreground mt-1">Emails queued for future delivery</p>
      </div>

      {messages.length === 0 ? (
        <EmptyState
          title="No scheduled emails"
          description="Emails you schedule for later delivery will appear here."
          icon={<Calendar className="h-5 w-5" />}
        />
      ) : (
        <>
          <div className="grid gap-2">
            {messages.map((msg) => (
              <Card key={msg.id} className="hover:bg-accent/20 transition-colors">
                <CardContent className="p-3 flex items-center gap-3">
                  <div className="shrink-0">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-violet-500/10">
                      <Send className="h-4 w-4 text-violet-400" />
                    </div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{msg.subject || '(no subject)'}</p>
                    <p className="text-xs text-muted-foreground">To: {msg.to?.map((r: any) => typeof r === 'string' ? r : r.email || r).join(', ')}</p>
                  </div>
                  <div className="shrink-0 flex items-center gap-3">
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Clock className="h-3.5 w-3.5 text-violet-400" />
                      <span className="hidden sm:inline">{formatScheduledTime(msg.scheduled_at)}</span>
                      <span className="sm:hidden">{msg.scheduled_at ? new Date(msg.scheduled_at).toLocaleDateString() : 'Unknown'}</span>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-red-400 hover:bg-red-500/10"
                      onClick={() => setCancelId(msg.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">{total} total</p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm">Page {page}</span>
              <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </>
      )}

      <Dialog open={!!cancelId} onOpenChange={(open) => { if (!open) setCancelId(null) }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-base font-semibold">Cancel scheduled email?</DialogTitle>
            <p className="text-sm text-muted-foreground">
              This will remove the email from the delivery queue. You cannot undo this action.
            </p>
          </DialogHeader>
          <div className="flex items-center justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={() => setCancelId(null)} disabled={canceling}>
              Keep
            </Button>
            <Button
              size="sm"
              variant="destructive"
              onClick={handleCancel}
              disabled={canceling}
              className="bg-red-500/90 hover:bg-red-600"
            >
              {canceling ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1.5" />}
              Cancel Send
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
